import { createClientId } from "../components/generalQuotationUtils";

export const DEFAULT_STAFF_LIMIT = 3;

export const normalizeStaffLimit = (value, fallback = DEFAULT_STAFF_LIMIT) => {
  const parsed = Number(value);
  const fallbackValue = Number.isFinite(Number(fallback))
    ? Number(fallback)
    : DEFAULT_STAFF_LIMIT;
  if (!Number.isFinite(parsed)) return Math.max(0, Math.floor(fallbackValue));
  return Math.max(0, Math.floor(parsed));
};

export const normalizeExtraStaffPurchased = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

export const TEAM_PERMISSION_DEFINITIONS = [
  {
    key: "quotationsCreate",
    label: "Create Quotations",
    description: "Start new curtain and general quotations.",
  },
  {
    key: "quotationsEdit",
    label: "Edit Quotations",
    description: "Update quotation details and pricing.",
  },
  {
    key: "quotationsView",
    label: "View Quotations",
    description: "Open quotation screens in the workspace.",
  },
  {
    key: "customerPdf",
    label: "Customer PDF",
    description: "Export customer-facing quotation PDFs.",
  },
  {
    key: "workshopPdf",
    label: "Workshop PDF",
    description: "Export workshop sheets and ZIP files.",
  },
  {
    key: "savedQuotes",
    label: "Saved Quotes",
    description: "Access the saved quotations area.",
  },
  {
    key: "dashboard",
    label: "Dashboard",
    description: "View business and CRM dashboard insights.",
  },
  {
    key: "crm",
    label: "CRM",
    description: "Access CRM pipelines, deals, and detail views.",
  },
  {
    key: "optimizer",
    label: "Roll Optimizer",
    description: "Use the roll and cutting optimizer.",
  },
  {
    key: "stitchingRatesTab",
    label: "Stitching & Tracks",
    description: "View and edit stitching and track rates.",
  },
  {
    key: "settings",
    label: "Settings",
    description: "Open workspace settings and preferences.",
  },
];

export const DEFAULT_TEAM_PERMISSIONS = {
  quotationsCreate: true,
  quotationsEdit: true,
  quotationsView: true,
  customerPdf: true,
  workshopPdf: false,
  savedQuotes: true,
  dashboard: false,
  crm: false,
  optimizer: false,
  stitchingRatesTab: false,
  settings: false,
};

export const OWNER_TEAM_PERMISSIONS = TEAM_PERMISSION_DEFINITIONS.reduce(
  (accumulator, permission) => ({
    ...accumulator,
    [permission.key]: true,
  }),
  {}
);

export const normalizePermissionSet = (permissions = {}) =>
  TEAM_PERMISSION_DEFINITIONS.reduce(
    (accumulator, permission) => ({
      ...accumulator,
      [permission.key]:
        permissions?.[permission.key] != null
          ? permissions[permission.key] === true
          : DEFAULT_TEAM_PERMISSIONS[permission.key] === true,
    }),
    {}
  );

export const normalizeEmailValue = (value = "") =>
  String(value || "").trim().toLowerCase();

const normalizeTeamMemberStatus = (member = {}) => {
  if (member?.status) {
    return String(member.status);
  }

  if (member?.uid) return "active";
  if (member?.isActive === false) return "disabled";
  if (member?.email) return "invited";
  return "draft";
};

export const createTeamMemberDraft = (member = {}) => {
  const status = normalizeTeamMemberStatus(member);
  const isActive =
    member?.isActive != null
      ? member.isActive === true
      : status === "active";

  return {
    id: member?.id || createClientId("team-member"),
    uid: String(member?.uid || ""),
    inviteId: String(member?.inviteId || member?.id || ""),
    workspaceId: String(member?.workspaceId || ""),
    name: String(member?.name || ""),
    phone: String(member?.phone || ""),
    email: String(member?.email || ""),
    role: String(member?.role || ""),
    status,
    isActive,
    permissions: normalizePermissionSet(member?.permissions),
    invitedBy: String(member?.invitedBy || ""),
    createdAt: member?.createdAt || null,
    updatedAt: member?.updatedAt || null,
    invitedAt: member?.invitedAt || null,
    lastInviteSentAt: member?.lastInviteSentAt || member?.inviteSentAt || null,
    acceptedAt: member?.acceptedAt || null,
    disabledAt: member?.disabledAt || null,
  };
};

export const normalizeTeamMembers = (members = []) =>
  (Array.isArray(members) ? members : [])
    .map((member) => createTeamMemberDraft(member))
    .filter((member) =>
      [
        member.name,
        member.phone,
        member.email,
        member.role,
        member.uid,
        member.status,
      ].some((value) => String(value || "").trim())
    );

export const countOccupiedStaffSlots = (members = []) =>
  normalizeTeamMembers(members).filter((member) =>
    ["active", "invited"].includes(String(member.status || ""))
  ).length;

export const isActiveTeamMember = (member) =>
  Boolean(member) &&
  member.isActive !== false &&
  String(member.status || "") === "active";

export const findMatchingTeamMember = (
  members = [],
  authUser,
  fallbackMemberId = ""
) => {
  const normalizedMembers = normalizeTeamMembers(members);
  const targetUid = String(authUser?.uid || "");
  const targetEmail = normalizeEmailValue(authUser?.email || "");

  return (
    normalizedMembers.find((member) => member.uid && member.uid === targetUid) ||
    normalizedMembers.find(
      (member) =>
        targetEmail &&
        normalizeEmailValue(member.email) === targetEmail
    ) ||
    normalizedMembers.find(
      (member) => fallbackMemberId && member.id === fallbackMemberId
    ) ||
    null
  );
};

export const buildOwnerWorkspaceProfile = (authUser) => ({
  workspaceId: String(authUser?.uid || ""),
  ownerUid: String(authUser?.uid || ""),
  memberId: String(authUser?.uid || ""),
  role: "owner",
  status: "active",
  permissions: OWNER_TEAM_PERMISSIONS,
  email: String(authUser?.email || ""),
  name: String(authUser?.displayName || ""),
});

export const normalizeWorkspaceProfile = (profile = {}, authUser = null) => {
  const ownerFallback = buildOwnerWorkspaceProfile(authUser);

  return {
    workspaceId: String(profile?.workspaceId || ownerFallback.workspaceId),
    ownerUid: String(profile?.ownerUid || ownerFallback.ownerUid),
    memberId: String(profile?.memberId || ""),
    role: profile?.role === "staff" ? "staff" : "owner",
    status: String(profile?.status || ownerFallback.status),
    permissions:
      profile?.role === "staff"
        ? normalizePermissionSet(profile?.permissions)
        : OWNER_TEAM_PERMISSIONS,
    email: String(profile?.email || authUser?.email || ""),
    name: String(profile?.name || authUser?.displayName || ""),
  };
};

export const getTeamMemberStatusLabel = (member) => {
  const status = String(member?.status || "");
  if (status === "invited") return "Invite Sent";
  if (status === "active") return "Active";
  if (status === "disabled") return "Disabled";
  return "Draft";
};

export const hasFeatureAccess = (permissions = {}, key) =>
  key ? normalizePermissionSet(permissions)[key] === true : false;
